import { Table } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";
import { CalendarOutlined, NumberOutlined } from "@ant-design/icons";

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "HSNCODE", label: "HSN Code" },
  { key: "QTY", label: "Qty", badge: "pieces" },
  { key: "AMOUNT", label: "Amount", badge: "netwt" },
];
const secondaryFields = [
  { key: "GST", label: "GST" },
  { key: "GSTAMT", label: "GST Amt" },
  { key: "CGSTAMT", label: "CGST Amt" },
  { key: "SGSTAMT", label: "SGST Amt" },
];

const HSNWiseSale = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ── fetch HSN wise sale for the selected date range ── */
  useEffect(() => {
    if (dates[0] && dates[1]) {
      const fromDate = dates[0].format("MM/DD/YYYY");
      const toDate = dates[1].format("MM/DD/YYYY");

      setLoading(true);
      setTableVisible(false);

      axios
        .get(
          `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=BILL_ITEMS_DET&where=BILLDATE>='${fromDate}'%20AND%20BILLDATE<='${toDate}'&order=BILLNO%2CSNO`,
          {
            headers: {
              tenantName: tenantName,
            },
          },
        )
        .then((response) => {
          const detailsData = Array.isArray(response.data) ? response.data : [];

          const hsnMap = {};
          detailsData.forEach((item) => {
            const hsnCode = String(item.HSNCODE || item.HsnCode || "Unassigned").trim();
            if (!hsnCode) return;

            const itemType = String(item?.TYPE || "").trim().toUpperCase();
            const salesType = String(item?.SALESTYPE || "").trim().toUpperCase();
            const isDeduction =
              itemType === "EB" ||
              itemType === "RB" ||
              salesType.includes("RETURN") ||
              salesType.includes("EXCHANGE");

            const rawQty = Number(item?.qty ?? item?.QTY ?? 0);
            const rawAmt = Number(item?.amount ?? item?.AMOUNT ?? 0);
            const rawGstAmt = Number(item?.GSTAMT ?? item?.gstamt ?? 0);
            const rawCgstAmt = Number(item?.CGSTAMT ?? item?.cgstamt ?? 0);
            const rawIgstAmt = Number(item?.IGSTAMT ?? item?.igstamt ?? 0);
            const rawSgstAmt = Number(item?.SGSTAMT ?? item?.sgstamt ?? 0);
            const rawGstRate = Number(item?.GST ?? item?.gst ?? 0);

            const qty = isDeduction ? -Math.abs(rawQty) : rawQty;
            const amt = isDeduction ? -Math.abs(rawAmt) : rawAmt;
            const gstAmt = isDeduction ? -Math.abs(rawGstAmt) : rawGstAmt;
            const cgstAmt = isDeduction ? -Math.abs(rawCgstAmt) : rawCgstAmt;
            const igstAmt = isDeduction ? -Math.abs(rawIgstAmt) : rawIgstAmt;
            const sgstAmt = isDeduction ? -Math.abs(rawSgstAmt) : rawSgstAmt;

            if (!hsnMap[hsnCode]) {
              hsnMap[hsnCode] = {
                HSNCODE: hsnCode,
                GST: rawGstRate,
                QTY: 0,
                AMOUNT: 0,
                GSTAMT: 0,
                CGSTAMT: 0,
                IGSTAMT: 0,
                SGSTAMT: 0,
              };
            }
            hsnMap[hsnCode].QTY += qty;
            hsnMap[hsnCode].AMOUNT += amt;
            hsnMap[hsnCode].GSTAMT += gstAmt;
            hsnMap[hsnCode].CGSTAMT += cgstAmt;
            hsnMap[hsnCode].IGSTAMT += igstAmt;
            hsnMap[hsnCode].SGSTAMT += sgstAmt;
          });

          const calculatedData = Object.values(hsnMap).map((row, index) => ({
            ...row,
            key: index + 1,
            serialNo: index + 1,
          }));

          setFilteredData(calculatedData);
          setTimeout(() => setTableVisible(true), 80);
        })
        .catch((error) => {
          console.error("Error fetching HSN wise sale:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [dates]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "HSN Code",
      dataIndex: "HSNCODE",
      key: "HSNCODE",
      align: "left",
      className: "col-tagno",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "QTY",
      dataIndex: "QTY",
      key: "QTY",
      align: "center",
      className: "col-pieces",
      render: (value) => <span className="piecesBadge">{value}</span>,
    },
    {
      title: "GST",
      dataIndex: "GST",
      key: "GST",
      align: "right",
    },
    {
      title: "GST AMT",
      dataIndex: "GSTAMT",
      key: "GSTAMT",
      align: "right",
    },
    {
      title: "CGST AMT",
      dataIndex: "CGSTAMT",
      key: "CGSTAMT",
      align: "right",
    },
    {
      title: "IGST AMT",
      dataIndex: "IGSTAMT",
      key: "IGSTAMT",
      align: "right",
    },
    {
      title: "SGST AMT",
      dataIndex: "SGSTAMT",
      key: "SGSTAMT",
      align: "right",
    },
    {
      title: "Amount",
      dataIndex: "AMOUNT",
      key: "AMOUNT",
      align: "right",
      className: "col-netwt",
      render: (value) => <span className="wtBadge">{value}</span>,
    },
  ];

  /* ── totals (blank values count as 0) ── */
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.qty += Number(item?.QTY || 0);
        acc.amount += Number(item?.AMOUNT || 0);
        acc.gst += Number(item?.GST || 0);
        acc.gstAmt += Number(item?.GSTAMT || 0);
        acc.cgst += Number(item?.CGSTAMT || 0);
        acc.igst += Number(item?.IGSTAMT || 0);
        acc.sgst += Number(item?.SGSTAMT || 0);
        return acc;
      },
      { qty: 0, amount: 0, gst: 0, gstAmt: 0, cgst: 0, igst: 0, sgst: 0 },
    );
  }, [filteredData]);

  const totalQty = totals.qty;
  const totalAmount = totals.amount.toFixed(2);
  const totGST = totals.gst.toFixed(2);
  const totalGST = totals.gstAmt.toFixed(2);
  const totalCGST = totals.cgst.toFixed(2);
  const totalIGST = totals.igst.toFixed(2);
  const totalSGST = totals.sgst.toFixed(2);

  /* rows shown in the table (no total row) */
  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        AMOUNT: Number(item?.AMOUNT || 0).toFixed(2),
        GSTAMT: Number(item?.GSTAMT || 0).toFixed(2),
        CGSTAMT: Number(item?.CGSTAMT || 0).toFixed(2),
        IGSTAMT: Number(item?.IGSTAMT || 0).toFixed(2),
        SGSTAMT: Number(item?.SGSTAMT || 0).toFixed(2),
        GST: Number(item?.GST || 0).toFixed(2),
      })),
    [filteredData],
  );

  /* export data = rows + total row */
  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      HSNCODE: "",
      QTY: totalQty,
      GST: totGST,
      GSTAMT: totalGST,
      CGSTAMT: totalCGST,
      IGSTAMT: totalIGST,
      SGSTAMT: totalSGST,
      AMOUNT: totalAmount,
    },
  ];

  return (
    <div className={styles.page}>
      <InventoryPageHeader
        title="HSN Wise Sale"
        subtitle="Point of Sale · HSN Wise Sale"
        icon={<NumberOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          {
            label: "Records",
            value: filteredData.length,
            color: "blue",
            decimals: 0,
          },
          { label: "Qty", value: totalQty, color: "orange", decimals: 0 },
          {
            label: "GST Amt",
            value: totals.gstAmt,
            color: "amber",
            decimals: 2,
          },
          {
            label: "Amount",
            value: totals.amount,
            color: "green",
            decimals: 2,
          },
        ]}
      />

      <div className={styles.toolbar} style={{ flexWrap: "wrap", rowGap: 12 }}>
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", rowGap: 12 }}
        >
          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Start Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[0]}
                onChange={(d) => setDates([d || dayjs(), dates[1]])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>

          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>End Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[1]}
                onChange={(d) => setDates([dates[0], d || dayjs()])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="HSNWiseSale"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="key"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No HSN wise sale records found for the selected dates"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell align="center">{totalQty}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalCGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalIGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalSGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalAmount}</Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <style>{`
  @keyframes mainProductPulse {
    0%   { box-shadow: 0 0 0 0 rgba(24,144,255,0.30); }
    70%  { box-shadow: 0 0 0 8px rgba(24,144,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(24,144,255,0); }
  }
  .mainProductHighlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 10px;
    border-radius: 14px;
    background: #eef6ff;
    border: 1.5px solid #65aefd;
    box-shadow: 0 0 0 1.5px #bcdcff inset;
    animation: mainProductPulse 2.4s infinite;
    min-width: 210px;
    flex-shrink: 0;
  }
  .mainProductIcon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(24,144,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1677ff;
    flex-shrink: 0;
  }
  .mainProductInner {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
  }
  .mainProductPicker.ant-picker {
    height: 34px !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d6e8ff !important;
    width: 100%;
  }
  .mainProductPicker.ant-picker:hover {
    border-color: #65aefd !important;
  }
  .mainProductPicker.ant-picker-focused {
    border-color: #1890ff !important;
    box-shadow: none !important;
  }
  .mainProductPicker .ant-picker-input > input {
    font-size: 13px;
    font-weight: 600;
    color: #1f2430;
  }
  .ant-table-thead > tr > th {
    white-space: nowrap !important;
  }
`}</style>
    </div>
  );
};

export default HSNWiseSale;